import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { inr, day, apiError, downloadCSV } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useDataTable, TableToolbar, TableFilterRow, TablePager, useRowPick, PickedChip } from '../components/DataTable';
import { useOpenMonths } from '../utils/months';

const PAID_VIA = ['cash', 'upi', 'card', 'bank', 'other'];
const emptyExpense = () => ({
  expenseDate: new Date().toISOString().slice(0, 10),
  category: '', locationId: '', amount: '', paidVia: 'cash', vendor: '', reference: '', note: '',
});

const ExpenseModal = ({ expense, categories, locations, onClose, onSaved }) => {
  const [form, setForm] = useState(expense ? {
    ...emptyExpense(), ...expense,
    locationId: expense.locationId == null ? '' : expense.locationId,
  } : { ...emptyExpense(), category: categories[0] || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // An expense belongs to the accounting month of its date, and closed months are
  // sealed — so bound the date picker to the open months and say so up front rather
  // than letting the save come back rejected.
  const { months, isOpen, loading: monthsLoading } = useOpenMonths();
  const lastOpen = months[0];
  const firstOpen = months[months.length - 1];
  const endOfMonth = (m) => (m ? `${m}-${String(new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0).getDate()).padStart(2, '0')}` : undefined);
  const dateClosed = !monthsLoading && !!form.expenseDate && !isOpen(form.expenseDate);

  const save = async (e) => {
    e.preventDefault();
    if (dateClosed) return setError(`${String(form.expenseDate).slice(0, 7)} is closed — expenses can only be booked into an open month.`);
    setBusy(true); setError('');
    try {
      const body = {
        expenseDate: form.expenseDate,
        category: form.category,
        locationId: form.locationId === '' ? null : Number(form.locationId),
        amount: Number(form.amount),
        paidVia: form.paidVia,
        vendor: form.vendor || null,
        reference: form.reference || null,
        note: form.note || null,
      };
      const { data } = expense
        ? await api.put(`/finance/expenses/${expense.id}`, body)
        : await api.post('/finance/expenses', body);
      toast.success('Saved'); onSaved(data.expense); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={expense ? `Edit Expense ${expense.code || ''}` : 'Add Expense'} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy || dateClosed}>{busy ? 'Saving…' : 'Save'}</button></>}>
      <form onSubmit={save}>
        <div className="row">
          <div className="field"><label>Date *</label>
            <input type="date" value={form.expenseDate} onChange={(e) => set('expenseDate', e.target.value)}
              min={firstOpen ? `${firstOpen}-01` : undefined} max={endOfMonth(lastOpen)} required />
            {dateClosed && <span className="form-error small">That month is closed — pick a date in {months.join(' / ') || 'an open month'}.</span>}
          </div>
          <div className="field"><label>Amount (₹) *</label><input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} required /></div>
        </div>
        <div className="row">
          <div className="field"><label>Category *</label>
            <select value={form.category} onChange={(e) => set('category', e.target.value)} required>
              <option value="" disabled>Select…</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="field"><label>Branch</label>
            <select value={form.locationId} onChange={(e) => set('locationId', e.target.value)}>
              <option value="">Central / Company-wide</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </div>
        <div className="row">
          <div className="field"><label>Paid via</label>
            <select value={form.paidVia} onChange={(e) => set('paidVia', e.target.value)}>
              {PAID_VIA.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
            </select>
          </div>
          <div className="field"><label>Vendor / Payee</label><input value={form.vendor || ''} onChange={(e) => set('vendor', e.target.value)} /></div>
        </div>
        <div className="field"><label>Reference</label><input value={form.reference || ''} onChange={(e) => set('reference', e.target.value)} placeholder="Bill / txn no." /></div>
        <div className="field"><label>Note</label><textarea rows={2} value={form.note || ''} onChange={(e) => set('note', e.target.value)} /></div>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Modal>
  );
};

const CategoriesModal = ({ categories, onClose, onSaved }) => {
  const [text, setText] = useState(categories.join('\n'));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const list = text.split('\n').map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error('Add at least one category'); return; }
    setBusy(true);
    try {
      const { data } = await api.put('/finance/expense-categories', { categories: list });
      toast.success('Categories updated'); onSaved(data.categories); onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Expense Categories" onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button></>}>
      <p className="muted small">One category per line. These appear when adding an expense and in the P&L breakdown.</p>
      <textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} style={{ width: '100%' }} />
    </Modal>
  );
};

const Expenses = () => {
  const { can } = useAuth();
  const canWrite = can('finance:write');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [category, setCategory] = useState('');
  const [locationId, setLocationId] = useState('');
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);
  const [showCats, setShowCats] = useState(false);

  useEffect(() => {
    api.get('/finance/expense-categories').then(({ data }) => setCategories(data.categories || [])).catch(() => {});
    api.get('/locations?all=1').then(({ data }) => setLocations(data.locations || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ month });
    if (category) params.set('category', category);
    if (locationId) params.set('locationId', locationId);
    api.get(`/finance/expenses?${params}`).then(({ data }) => setRows(data.expenses || []))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [month, category, locationId]);
  useEffect(load, [load]);

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const del = async (row) => {
    if (!window.confirm(`Delete expense ${row.code} (${inr(row.amount)})?`)) return;
    try { await api.delete(`/finance/expenses/${row.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportCsv = () => downloadCSV(`expenses-${month}.csv`,
    ['Date', 'Code', 'Category', 'Branch', 'Vendor', 'Paid Via', 'Reference', 'Amount'],
    rows.map((r) => [day(r.expenseDate), r.code, r.category, r.location?.name || 'Central', r.vendor || '', r.paidVia, r.reference || '', r.amount]));

  const dt = useDataTable(rows, {
    searchPlaceholder: 'Search vendor, note, category, reference…',
    searchKeys: ['category', 'vendor', 'reference', 'note', 'code'],
    columns: [
      { key: 'code', label: 'Code', value: (r) => r.code, placeholder: 'Code' },
      { key: 'category', label: 'Category', value: (r) => r.category, type: 'select' },
      { key: 'branch', label: 'Branch', value: (r) => r.location?.name || 'Central', type: 'select' },
      { key: 'vendor', label: 'Vendor', value: (r) => r.vendor, placeholder: 'Vendor' },
      { key: 'paidVia', label: 'Paid', value: (r) => (r.paidVia || '').toUpperCase(), type: 'select' },
    ],
    filters: [
      { key: 'category', label: 'Category', value: (r) => r.category },
      { key: 'paidVia', label: 'Paid Via', value: (r) => (r.paidVia || '').toUpperCase() },
      { key: 'branch', label: 'Branch', value: (r) => r.location?.name || 'Central' },
    ],
  });
  const pick = useRowPick();

  return (
    <div>
      <div className="section-head" style={{ flexWrap: 'wrap', gap: '.5rem' }}>
        <div className="flex gap" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">All branches</option>
            <option value="central">Central / Company-wide</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
      </div>

      <TableToolbar dt={dt} right={<>
        <PickedChip pick={pick} />
        <button className="btn btn-outline btn-sm" onClick={exportCsv} disabled={!rows.length}>Export CSV</button>
        {canWrite && <button className="btn btn-outline btn-sm" onClick={() => setShowCats(true)}>Categories</button>}
        {canWrite && <button className="btn btn-primary" onClick={() => setEditing(null)}>+ Add Expense</button>}
      </>} />

      <div className="muted small" style={{ marginBottom: '.75rem' }}>
        {rows.length} expense(s) · Total <strong>{inr(total)}</strong> for {month}
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr><th className="freeze-col">Date</th><th>Code</th><th>Category</th><th>Branch</th><th>Vendor</th><th>Paid</th><th className="right">Amount</th>{canWrite && <th></th>}</tr>
              <TableFilterRow dt={dt} cells={[null, 'code', 'category', 'branch', 'vendor', 'paidVia', null, ...(canWrite ? [null] : [])]} />
            </thead>
            <tbody>
              {dt.rows.map((r) => (
                <tr key={r.id} {...pick.rowProps(r.id)}>
                  <td className="freeze-col">{day(r.expenseDate)}</td>
                  <td className="muted small">{r.code}</td>
                  <td>{r.category}</td>
                  <td>{r.location?.name || <span className="muted">Central</span>}</td>
                  <td>{r.vendor || <span className="muted">—</span>}</td>
                  <td className="muted small">{(r.paidVia || '').toUpperCase()}</td>
                  <td className="right">{inr(r.amount)}</td>
                  {canWrite && <td className="right nowrap">
                    <button className="btn btn-outline btn-sm" onClick={(ev) => { ev.stopPropagation(); setEditing(r); }}>Edit</button>{' '}
                    <button className="btn btn-outline btn-sm" onClick={(ev) => { ev.stopPropagation(); del(r); }}>Delete</button>
                  </td>}
                </tr>
              ))}
              {dt.rows.length === 0 && <tr><td colSpan={canWrite ? 8 : 7} className="empty">{dt.isFiltered ? 'No expenses match your search.' : `No expenses recorded for ${month}.`}</td></tr>}
            </tbody>
            <tfoot><tr><td colSpan={6} className="right"><strong>Total</strong></td><td className="right"><strong>{inr(total)}</strong></td>{canWrite && <td></td>}</tr></tfoot>
          </table>
        </div>
      )}
      {!loading && <TablePager dt={dt} />}

      {editing !== undefined && <ExpenseModal expense={editing} categories={categories} locations={locations} onClose={() => setEditing(undefined)} onSaved={load} />}
      {showCats && <CategoriesModal categories={categories} onClose={() => setShowCats(false)} onSaved={setCategories} />}
    </div>
  );
};

export default Expenses;
